// Compliance Actions Demo Service
// Provides mock data and operations for compliance actions in demo mode

import { format, addDays, subDays } from 'date-fns';

// ============================================================================
// TYPES
// ============================================================================

export interface InspectionRecord {
  id: number;
  assetComplianceId: number;
  inspectionDate: string;
  inspectorName: string;
  outcome: 'Pass' | 'Fail' | 'PassWithAdvisory' | 'ConditionalPass';
  notes?: string;
  cost?: number;
  serviceProvider?: string;
  nextInspectionDate?: string;
  createdBy: string;
  createdDate: string;
  files?: Array<{
    id: number;
    fileName: string;
    fileSize: number;
    uploadedDate: string;
  }>;
}

export interface InspectionSchedule {
  id: number;
  assetComplianceId: number;
  scheduledDate: string;
  assignedInspector?: string;
  schedulingNotes?: string;
  notificationSent: boolean;
  createdBy: string;
  createdDate: string;
}

export interface ComplianceExemption {
  id: number;
  assetComplianceId: number;
  reason: string;
  detailedExplanation: string;
  effectiveDate: string;
  expiryDate?: string;
  reviewedBy?: string;
  createdBy: string;
  createdDate: string;
  files?: Array<{
    id: number;
    fileName: string;
    fileSize: number;
    uploadedDate: string;
  }>;
}

export interface ComplianceHistoryEvent {
  id: number;
  assetComplianceId: number;
  eventType: string;
  eventDate: string;
  eventDetails: string;
  performedBy: string;
  beforeValue?: string;
  afterValue?: string;
  evidenceFiles?: Array<{
    id: number;
    fileName: string;
    fileSize: number;
    uploadedDate: string;
  }>;
}

export interface DeferralRecord {
  id: number;
  assetComplianceId: number;
  currentDueDate: string;
  newDueDate: string;
  reason: string;
  approvalRequired: boolean;
  approver?: string;
  approved?: boolean;
  createdBy: string;
  createdDate: string;
}

// ============================================================================
// MOCK DATA STORAGE
// ============================================================================

class ComplianceActionsDataStore {
  private inspections: InspectionRecord[] = [];
  private schedules: InspectionSchedule[] = [];
  private exemptions: ComplianceExemption[] = [];
  private history: ComplianceHistoryEvent[] = [];
  private deferrals: DeferralRecord[] = [];
  private nextId = 1000;

  constructor() {
    this.initializeMockData();
  }

  private initializeMockData() {
    // Initialize with some sample data
    const now = new Date();

    // Sample inspections
    this.inspections = [
      {
        id: 1,
        assetComplianceId: 1,
        inspectionDate: format(subDays(now, 30), 'yyyy-MM-dd'),
        inspectorName: 'John Smith',
        outcome: 'Pass',
        notes: 'All safety checks completed successfully',
        cost: 250.00,
        serviceProvider: 'Safety First Ltd',
        nextInspectionDate: format(addDays(now, 335), 'yyyy-MM-dd'),
        createdBy: 'admin',
        createdDate: format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm:ss"),
        files: [
          {
            id: 1,
            fileName: 'inspection-report.pdf',
            fileSize: 245678,
            uploadedDate: format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm:ss"),
          },
        ],
      },
      {
        id: 2,
        assetComplianceId: 2,
        inspectionDate: format(subDays(now, 15), 'yyyy-MM-dd'),
        inspectorName: 'Sarah Johnson',
        outcome: 'PassWithAdvisory',
        notes: 'Minor wear noted on safety harness, recommend replacement within 3 months',
        cost: 180.00,
        serviceProvider: 'Compliance Experts',
        nextInspectionDate: format(addDays(now, 75), 'yyyy-MM-dd'),
        createdBy: 'admin',
        createdDate: format(subDays(now, 15), "yyyy-MM-dd'T'HH:mm:ss"),
      },
    ];

    // Sample schedules
    this.schedules = [
      {
        id: 1,
        assetComplianceId: 3,
        scheduledDate: format(addDays(now, 7), 'yyyy-MM-dd'),
        assignedInspector: 'Mike Williams',
        schedulingNotes: 'Quarterly safety inspection',
        notificationSent: true,
        createdBy: 'admin',
        createdDate: format(now, "yyyy-MM-dd'T'HH:mm:ss"),
      },
    ];

    // Sample history events
    this.history = [
      {
        id: 1,
        assetComplianceId: 1,
        eventType: 'InspectionRecorded',
        eventDate: format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm:ss"),
        eventDetails: 'Annual safety inspection completed. All checks passed.',
        performedBy: 'John Smith',
        afterValue: 'Pass',
        evidenceFiles: [
          {
            id: 1,
            fileName: 'inspection-report.pdf',
            fileSize: 245678,
            uploadedDate: format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm:ss"),
          },
        ],
      },
      {
        id: 2,
        assetComplianceId: 1,
        eventType: 'InspectionScheduled',
        eventDate: format(subDays(now, 35), "yyyy-MM-dd'T'HH:mm:ss"),
        eventDetails: 'Inspection scheduled for ' + format(subDays(now, 30), 'PPP'),
        performedBy: 'Sarah Johnson',
      },
      {
        id: 3,
        assetComplianceId: 2,
        eventType: 'StatusChanged',
        eventDate: format(subDays(now, 20), "yyyy-MM-dd'T'HH:mm:ss"),
        eventDetails: 'Compliance status updated',
        performedBy: 'Mike Williams',
        beforeValue: 'Overdue',
        afterValue: 'Compliant',
      },
    ];
  }

  // ============================================================================
  // INSPECTION OPERATIONS
  // ============================================================================

  recordInspection(data: Omit<InspectionRecord, 'id' | 'createdDate'>): InspectionRecord {
    const inspection: InspectionRecord = {
      ...data,
      id: this.nextId++,
      createdDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    };
    this.inspections.push(inspection);

    // Add to history
    this.addHistoryEvent({
      assetComplianceId: data.assetComplianceId,
      eventType: 'InspectionRecorded',
      eventDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      eventDetails: `Inspection completed with outcome: ${data.outcome}`,
      performedBy: data.createdBy,
      afterValue: data.outcome,
      evidenceFiles: data.files,
    });

    return inspection;
  }

  getInspectionsByCompliance(assetComplianceId: number): InspectionRecord[] {
    return this.inspections.filter((i) => i.assetComplianceId === assetComplianceId);
  }

  // ============================================================================
  // SCHEDULE OPERATIONS
  // ============================================================================

  scheduleInspection(data: Omit<InspectionSchedule, 'id' | 'createdDate'>): InspectionSchedule {
    const schedule: InspectionSchedule = {
      ...data,
      id: this.nextId++,
      createdDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    };
    this.schedules.push(schedule);

    // Add to history
    this.addHistoryEvent({
      assetComplianceId: data.assetComplianceId,
      eventType: 'InspectionScheduled',
      eventDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      eventDetails: `Inspection scheduled for ${format(new Date(data.scheduledDate), 'PPP')}`,
      performedBy: data.createdBy,
    });

    return schedule;
  }

  getSchedulesByCompliance(assetComplianceId: number): InspectionSchedule[] {
    return this.schedules.filter((s) => s.assetComplianceId === assetComplianceId);
  }

  // ============================================================================
  // EXEMPTION OPERATIONS
  // ============================================================================

  markNonApplicable(data: Omit<ComplianceExemption, 'id' | 'createdDate'>): ComplianceExemption {
    const exemption: ComplianceExemption = {
      ...data,
      id: this.nextId++,
      createdDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    };
    this.exemptions.push(exemption);

    // Add to history
    this.addHistoryEvent({
      assetComplianceId: data.assetComplianceId,
      eventType: 'MarkedNonApplicable',
      eventDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      eventDetails: `Marked as non-applicable: ${data.reason}`,
      performedBy: data.createdBy,
      evidenceFiles: data.files,
    });

    return exemption;
  }

  reverseExemption(exemptionId: number, performedBy: string): boolean {
    const index = this.exemptions.findIndex((e) => e.id === exemptionId);
    if (index === -1) return false;

    const exemption = this.exemptions[index];
    this.exemptions.splice(index, 1);

    // Add to history
    this.addHistoryEvent({
      assetComplianceId: exemption.assetComplianceId,
      eventType: 'StatusChanged',
      eventDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      eventDetails: 'Exemption reversed, compliance reactivated',
      performedBy,
      beforeValue: 'Non-Applicable',
      afterValue: 'Active',
    });

    return true;
  }

  getExemptionsByCompliance(assetComplianceId: number): ComplianceExemption[] {
    return this.exemptions.filter((e) => e.assetComplianceId === assetComplianceId);
  }

  // ============================================================================
  // DEFERRAL OPERATIONS
  // ============================================================================

  deferDueDate(data: Omit<DeferralRecord, 'id' | 'createdDate'>): DeferralRecord {
    const deferral: DeferralRecord = {
      ...data,
      id: this.nextId++,
      createdDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    };
    this.deferrals.push(deferral);

    // Add to history
    this.addHistoryEvent({
      assetComplianceId: data.assetComplianceId,
      eventType: 'DueDateDeferred',
      eventDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      eventDetails: data.reason,
      performedBy: data.createdBy,
      beforeValue: format(new Date(data.currentDueDate), 'PPP'),
      afterValue: format(new Date(data.newDueDate), 'PPP'),
    });

    return deferral;
  }

  getDeferralsByCompliance(assetComplianceId: number): DeferralRecord[] {
    return this.deferrals.filter((d) => d.assetComplianceId === assetComplianceId);
  }

  // ============================================================================
  // HISTORY OPERATIONS
  // ============================================================================

  addHistoryEvent(data: Omit<ComplianceHistoryEvent, 'id'>): ComplianceHistoryEvent {
    const event: ComplianceHistoryEvent = {
      ...data,
      id: this.nextId++,
    };
    this.history.push(event);
    return event;
  }

  getHistoryByCompliance(assetComplianceId: number): ComplianceHistoryEvent[] {
    return this.history
      .filter((h) => h.assetComplianceId === assetComplianceId)
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
  }

  getAllHistory(): ComplianceHistoryEvent[] {
    return this.history.sort(
      (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
    );
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  reset() {
    this.inspections = [];
    this.schedules = [];
    this.exemptions = [];
    this.history = [];
    this.deferrals = [];
    this.nextId = 1000;
    this.initializeMockData();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const complianceActionsDataStore = new ComplianceActionsDataStore();

// ============================================================================
// SERVICE METHODS
// ============================================================================

export const complianceActionsDemoService = {
  // Inspections
  recordInspection: async (data: {
    assetComplianceId: number;
    inspectionDate: Date;
    inspectorName: string;
    outcome: 'Pass' | 'Fail' | 'PassWithAdvisory' | 'ConditionalPass';
    notes?: string;
    cost?: string;
    serviceProvider?: string;
    nextInspectionDate?: Date;
    files: File[];
  }): Promise<InspectionRecord> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Convert files to mock file records
    const fileRecords = data.files.map((file, index) => ({
      id: Date.now() + index,
      fileName: file.name,
      fileSize: file.size,
      uploadedDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    }));

    return complianceActionsDataStore.recordInspection({
      assetComplianceId: data.assetComplianceId,
      inspectionDate: format(data.inspectionDate, 'yyyy-MM-dd'),
      inspectorName: data.inspectorName,
      outcome: data.outcome,
      notes: data.notes,
      cost: data.cost ? parseFloat(data.cost) : undefined,
      serviceProvider: data.serviceProvider,
      nextInspectionDate: data.nextInspectionDate
        ? format(data.nextInspectionDate, 'yyyy-MM-dd')
        : undefined,
      createdBy: 'current-user',
      files: fileRecords,
    });
  },

  scheduleInspection: async (data: {
    assetComplianceId: number;
    newInspectionDate: Date;
    assignedInspector?: string;
    schedulingNotes?: string;
    sendNotification: boolean;
  }): Promise<InspectionSchedule> => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    return complianceActionsDataStore.scheduleInspection({
      assetComplianceId: data.assetComplianceId,
      scheduledDate: format(data.newInspectionDate, 'yyyy-MM-dd'),
      assignedInspector: data.assignedInspector,
      schedulingNotes: data.schedulingNotes,
      notificationSent: data.sendNotification,
      createdBy: 'current-user',
    });
  },

  markNonApplicable: async (data: {
    assetComplianceId: number;
    reason: string;
    detailedExplanation: string;
    effectiveDate: Date;
    expiryDate?: Date;
    reviewedBy?: string;
    files: File[];
  }): Promise<ComplianceExemption> => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const fileRecords = data.files.map((file, index) => ({
      id: Date.now() + index,
      fileName: file.name,
      fileSize: file.size,
      uploadedDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    }));

    return complianceActionsDataStore.markNonApplicable({
      assetComplianceId: data.assetComplianceId,
      reason: data.reason,
      detailedExplanation: data.detailedExplanation,
      effectiveDate: format(data.effectiveDate, 'yyyy-MM-dd'),
      expiryDate: data.expiryDate ? format(data.expiryDate, 'yyyy-MM-dd') : undefined,
      reviewedBy: data.reviewedBy,
      createdBy: 'current-user',
      files: fileRecords,
    });
  },

  deferDueDate: async (data: {
    assetComplianceId: number;
    currentDueDate: string;
    newDueDate: Date;
    reason: string;
    approvalRequired: boolean;
    approver?: string;
  }): Promise<DeferralRecord> => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    return complianceActionsDataStore.deferDueDate({
      assetComplianceId: data.assetComplianceId,
      currentDueDate: data.currentDueDate,
      newDueDate: format(data.newDueDate, 'yyyy-MM-dd'),
      reason: data.reason,
      approvalRequired: data.approvalRequired,
      approver: data.approver,
      createdBy: 'current-user',
    });
  },

  getHistory: async (assetComplianceId: number): Promise<ComplianceHistoryEvent[]> => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return complianceActionsDataStore.getHistoryByCompliance(assetComplianceId);
  },

  reverseExemption: async (exemptionId: number): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return complianceActionsDataStore.reverseExemption(exemptionId, 'current-user');
  },
};
